import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Note {
  id: string;
  contact_id?: string;
  deal_id?: string;
  lead_id?: string;
  title?: string;
  content: string;
  created_at: string;
  created_by?: string;
  modified_at?: string;
  modified_by?: string;
  creator?: {
    display_name: string;
    avatar_url?: string;
  };
}

export function useNotes(params: { contactId?: string; dealId?: string; leadId?: string }) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadNotes = async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('notes')
        .select(`
          *,
          creator:app_users!notes_created_by_fkey(display_name, avatar_url)
        `)
        .order('created_at', { ascending: false });

      if (params.contactId) {
        query = query.eq('contact_id', params.contactId);
      } else if (params.dealId) {
        query = query.eq('deal_id', params.dealId);
      } else if (params.leadId) {
        query = query.eq('lead_id', params.leadId);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;
      setNotes(data || []);
    } catch (err) {
      console.error('Error loading notes:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  const createNote = async (noteData: { title?: string; content: string }) => {
    try {
      const { data: currentUser } = await supabase.auth.getUser();
      const { data: appUser } = await supabase
        .from('app_users')
        .select('id')
        .eq('auth_user_id', currentUser.user?.id)
        .single();

      const newNote = {
        ...noteData,
        contact_id: params.contactId || null,
        deal_id: params.dealId || null,
        lead_id: params.leadId || null,
        created_by: appUser?.id || null,
      };

      const { data, error } = await supabase
        .from('notes')
        .insert(newNote)
        .select()
        .single();

      if (error) throw error;
      await loadNotes();
      return data;
    } catch (err) {
      console.error('Error creating note:', err);
      toast.error('Failed to create note');
      throw err;
    }
  };

  const updateNote = async (id: string, updates: { title?: string; content?: string }) => {
    try {
      const { data: currentUser } = await supabase.auth.getUser();
      const { data: appUser } = await supabase
        .from('app_users')
        .select('id')
        .eq('auth_user_id', currentUser.user?.id)
        .single();

      const { error } = await supabase
        .from('notes')
        .update({
          ...updates,
          modified_at: new Date().toISOString(),
          modified_by: appUser?.id || null,
        })
        .eq('id', id);

      if (error) throw error;
      await loadNotes();
    } catch (err) {
      console.error('Error updating note:', err);
      toast.error('Failed to update note');
      throw err;
    }
  };

  const deleteNote = async (id: string) => {
    try {
      const { error } = await supabase
        .from('notes')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await loadNotes();
    } catch (err) {
      console.error('Error deleting note:', err);
      toast.error('Failed to delete note');
      throw err;
    }
  };

  useEffect(() => {
    loadNotes();
  }, [params.contactId, params.dealId, params.leadId]);

  return {
    notes,
    loading,
    error,
    loadNotes,
    createNote,
    updateNote,
    deleteNote,
  };
}
